/** The booking header's immediate actions (plan §3 "Immediate") — every group in plan
 *  order, each item present only with its permission (absent otherwise: the menu never
 *  advertises power the operator lacks) and disabled, with the reason, when its flag or
 *  the booking says not now:
 *
 *  - **Communication** — Resend trip invite… (`console_events_emit`; guest-visible, so it
 *    confirms, with the Emit / Suppress choice for tour_participant.created; disabled for
 *    a booking with no guest), Emit event… (`console_events_emit`): the grids'
 *    `EmitEventDialog` over a one-booking selection.
 *  - **<Restech>** (or "Integration" for a booking no restech feeds) — Refresh from
 *    <restech>… (`console_bookings_refresh`, the `restech_refresh_enabled` kill switch;
 *    disabled while the draft is dirty — "Save or discard first" — or when the restech
 *    cannot be asked for one booking); View raw <restech> data
 *    (`console_bookings_restech_raw`; the Integration tab, reading live); What the feed
 *    sent (view; the Integration tab); Reimport from stored XML… (`console_bookings_reimport`,
 *    the grid's `ReimportXmlDialog`).
 *  - **Cache** — Inspect mobile app cache (the Cache tab), then the refresh and the
 *    rebuilds of `useBookingCacheActions` (which the Cache tab's buttons share).
 *  - **Diagnose** — AI diagnose (`console_entity_ai`): the Diagnose tab.
 *
 *  `host` renders what the items open: the confirmations on the Actions button (or the
 *  Cache tab's button), the dialogs. A queued refresh's task is watched here, not in the
 *  popover, so the record re-seeds when it finishes even if the popover was closed. */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import * as bgtasksApi from '@/api/bgtasks';
import { isActiveState, TASK_STATE } from '@/api/bgtasks';
import { bookingActionKeys, type BookingRestechRefreshAccepted } from '@/api/bookingActions';
import { bookingRecordKeys, type BookingRecordEditContext, type BookingRestechUnsupportedReason } from '@/api/bookingRecord';
import { bookingDetailKey } from '@/api/bookings';
import { useBatchAction } from '@/components/DataGrid/useBatchAction';
import { useSnackbar } from '@/components/Snackbar';
import { openBackgroundTask } from '@/features/bgtasks/openBackgroundTask';
import { EmitEventDialog } from '@/features/emit-events/EmitEventDialog';
import { singleRecordBatchContext } from '@/features/records/actions/singleRecordBatch';
import { useDraftSummary } from '@/features/records/draft/DraftProvider';
import type { RecordActionGroup, RecordActionItem } from '@/features/records/page/RecordActionsMenu';
import { usePermissions } from '@/hooks/usePermissions';

import { ReimportXmlDialog } from '../ReimportXmlDialog';

import { InvitePopover } from './InvitePopover';
import { RestechRefreshPopover } from './RestechRefreshPopover';
import { cacheMenuItems, useBookingCacheActions, type BookingCacheActions } from './useBookingCacheActions';

export const PERM = {
  emit: 'console_events_emit',
  refresh: 'console_bookings_refresh',
  raw: 'console_bookings_restech_raw',
  reimport: 'console_bookings_reimport',
  ai: 'console_entity_ai',
} as const;

const TASK_POLL_MS = 5000;

/** Why the restech cannot be asked for this booking, in a menu line. */
function unsupportedText(reason: BookingRestechUnsupportedReason | null, name: string): string {
  switch (reason) {
    case 'not_supported':
      return `${name} bookings can’t be fetched one at a time — reimport from stored XML instead`;
    case 'no_booking_id':
      return `No ${name} booking id to ask for`;
    case 'not_configured':
      return `The tenant’s ${name} integration can’t be used`;
    default:
      return 'Not fed by a reservation system';
  }
}

/** Polls a refresh task until it ends — the same query the popover's TaskProgressCard
 *  reads, so the two share one request. */
function useTaskEnd(taskId: string | null, onEnd: (task: bgtasksApi.BackgroundTaskDetail) => void) {
  const q = useQuery({
    queryKey: ['bgtask', taskId],
    queryFn: () => bgtasksApi.retrieve(taskId!),
    enabled: taskId !== null,
    refetchInterval: (query) => (query.state.data && isActiveState(query.state.data.state) ? TASK_POLL_MS : false),
  });
  const ended = useRef<string | null>(null);
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;
  const task = q.data;
  useEffect(() => {
    if (!task || !taskId || ended.current === taskId || isActiveState(task.state)) return;
    ended.current = taskId;
    onEndRef.current(task);
  }, [task, taskId]);
}

export type BookingTabTarget = 'integration' | 'cache' | 'diagnose';

export function useBookingImmediateActions({
  bookingId,
  editContext,
  onTab,
  onIntegration,
  guestName,
}: {
  bookingId: number;
  editContext: BookingRecordEditContext;
  onTab: (tab: BookingTabTarget) => void;
  /** Open the Integration tab at a section; `fetch` asks for the live raw read. */
  onIntegration: (section: 'feed' | 'raw', fetch?: boolean) => void;
  /** The guest's name, for the invitation's confirmation. */
  guestName: string;
}): { groups: RecordActionGroup[]; host: ReactNode; cache: BookingCacheActions } {
  const { hasPerm } = usePermissions();
  const { count } = useDraftSummary();
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const tenantId = editContext.tenant.id;
  const { restech } = editContext;
  const managed = Boolean(restech.managed_by);
  const name = restech.managed_by_display ?? restech.managed_by ?? 'the booking system';

  const [dialog, setDialog] = useState<'emit' | 'reimport' | null>(null);
  const closeDialog = useCallback(() => setDialog(null), []);
  const [refreshAnchor, setRefreshAnchor] = useState<HTMLElement | null>(null);
  const popoverOpen = useRef(false);
  popoverOpen.current = refreshAnchor !== null;
  const [refreshTask, setRefreshTask] = useState<string | null>(null);
  const [inviteAnchor, setInviteAnchor] = useState<HTMLElement | null>(null);
  const { cache, host: cacheHost } = useBookingCacheActions({ bookingId, editContext });

  const reimport = useBatchAction({
    resource: 'bookings',
    queryKey: ['bookings', 'imports', bookingId],
    onCleared: closeDialog,
  });

  useTaskEnd(refreshTask, (task) => {
    if (task.state === TASK_STATE.COMPLETED) {
      for (const key of [
        bookingDetailKey(bookingId),
        bookingRecordKeys.editContext(bookingId),
        bookingActionKeys.feed(bookingId),
        bookingActionKeys.timeline(bookingId),
        ['bookings', 'imports', bookingId],
        ['bookings', 'api-requests', bookingId],
      ]) {
        void qc.invalidateQueries({ queryKey: key });
      }
      if (!popoverOpen.current) snackbar.show(`Refreshed from ${name} — the record reloaded.`, 'success');
    } else if (!popoverOpen.current) {
      snackbar.show(`Refresh from ${name} did not finish${task.error_message ? `: ${task.error_message}` : '.'}`, 'error', {
        autoHideDuration: 15_000,
        action: { label: 'View task', onClick: () => openBackgroundTask(task.id) },
      });
    }
  });

  const communication: RecordActionItem[] = [];
  if (hasPerm(PERM.emit)) {
    communication.push({
      key: 'invite',
      label: 'Resend trip invite…',
      ...(editContext.guest ? { hint: 'guest-visible' } : { disabled: true, description: 'The booking has no guest' }),
      onSelect: (anchorEl) => setInviteAnchor(anchorEl),
    });
    communication.push({ key: 'emit_event', label: 'Emit event…', onSelect: () => setDialog('emit') });
  }

  const integration: RecordActionItem[] = [];
  if (managed && hasPerm(PERM.refresh)) {
    const why = !editContext.flags.restech_refresh_enabled
      ? 'Switched off on this environment'
      : !restech.refresh.supported
        ? unsupportedText(restech.refresh.reason, name)
        : count > 0
          ? 'Save or discard first — the draft has unsaved changes'
          : null;
    integration.push({
      key: 'restech_refresh',
      label: `Refresh from ${name}…`,
      ...(why ? { disabled: true, description: why } : { hint: 'background task' }),
      onSelect: (anchorEl) => setRefreshAnchor(anchorEl),
    });
  }
  if (managed && hasPerm(PERM.raw)) {
    integration.push({
      key: 'restech_raw',
      label: `View raw ${name} data`,
      hint: 'audited read',
      onSelect: () => onIntegration('raw', true),
    });
  }
  integration.push({ key: 'feed', label: 'What the feed sent', onSelect: () => onIntegration('feed') });
  if (hasPerm(PERM.reimport)) {
    integration.push({ key: 'reimport_xml', label: 'Reimport from stored XML…', onSelect: () => setDialog('reimport') });
  }

  const diagnose: RecordActionItem[] = [];
  if (hasPerm(PERM.ai)) diagnose.push({ key: 'ai_diagnose', label: 'AI diagnose', onSelect: () => onTab('diagnose') });

  const groups: RecordActionGroup[] = [
    { key: 'communication', label: 'Communication', items: communication },
    { key: 'integration', label: managed ? name : 'Integration', items: integration },
    { key: 'cache', label: 'Cache', items: cacheMenuItems(cache, () => onTab('cache')) },
    { key: 'diagnose', label: 'Diagnose', items: diagnose },
  ];

  const ctx = singleRecordBatchContext({
    id: bookingId,
    tenantId,
    close: closeDialog,
    runStandard: (params) =>
      reimport.run({
        action: 'reimport_xml',
        label: 'Reimport from stored XML',
        selection: { mode: 'ids', ids: [bookingId] },
        ...(params ? { params } : {}),
      }),
  });

  const host = (
    <>
      {refreshAnchor && (
        <RestechRefreshPopover
          bookingId={bookingId}
          editContext={editContext}
          anchorEl={refreshAnchor}
          onClose={() => setRefreshAnchor(null)}
          onQueued={(r: BookingRestechRefreshAccepted) => setRefreshTask(r.task_id)}
          onReimport={
            hasPerm(PERM.reimport)
              ? () => {
                  setRefreshAnchor(null);
                  setDialog('reimport');
                }
              : undefined
          }
        />
      )}
      {inviteAnchor && (
        <InvitePopover
          bookingId={bookingId}
          guestName={guestName}
          anchorEl={inviteAnchor}
          onClose={() => setInviteAnchor(null)}
        />
      )}
      {cacheHost}
      {dialog === 'emit' && <EmitEventDialog ctx={ctx} grid="bookings" />}
      {dialog === 'reimport' && <ReimportXmlDialog ctx={ctx} />}
    </>
  );

  return { groups, host, cache };
}
